import React, { useState, useEffect, useContext } from "react";
import "../../App.css"; // Import global styles
import { UserContext } from "../context/UserContext"; // Assuming you have a UserContext
// import { useLoading } from "../context/LoadingContext"; // Commented out
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faTimes } from "@fortawesome/free-solid-svg-icons";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons"; // Import faEnvelope from free-solid-svg-icons
import {
  faFacebook,
  faWhatsapp,
  faInstagram,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons"; // Import other brand icons
import html2canvas from "html2canvas"; // Import html2canvas
import { FaUserTie } from 'react-icons/fa';
import VoteCard from "./Vote_Card"; // Add this import

const uploadToBackend = async (imageDataUrl) => {
  const blob = await fetch(imageDataUrl).then((res) => res.blob());
  const formData = new FormData();
  formData.append("media", blob, "leaderboard.png"); // ✅ Change "image" to "media"

  const response = await fetch("/api/upload-s3", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to upload image");
  }

  const data = await response.json();
  return data.mediaUrl; // ✅ Ensure this is used in the frontend
};

// Mapping of party abbreviations to their full forms
const partyFullForm = {
  ALP: "Australian Labor Party",
  LP: "Liberal Party of Australia",
  NATS: "National Party of Australia",
  AG: "Australian Greens",
  PHON: "Pauline Hanson's One Nation",
  KAP: "Katter's Australian Party",
  CA: "Centre Alliance",
  UAP: "United Australia Party",
  LNP: "Liberal National Party of Queensland",
  CLP: "Country Liberal Party",
  SFF: "Shooters, Fishers and Farmers Party",
  AJP: "Animal Justice Party",
  AD: "Australian Democrats",
  CDP: "Christian Democratic Party",
  DLP: "Democratic Labour Party",
  RA: "Reason Australia",
  SP: "Science Party",
  SPA: "Secular Party of Australia",
  SAP: "Sustainable Australia Party",
  PPAU: "Pirate Party Australia",
  AP: "Australian Progressives",
  AC: "Australian Conservatives",
  ACR: "Australian Christians",
  ACP: "Australian Cyclists Party",
  AMEP: "Australian Motoring Enthusiast Party",
  ARFP: "Australian Recreational Fishers Party",
  ASP: "Australian Sports Party",
  AVP: "Australian Voice Party",
  AWP: "Australian Workers Party",
  AYCC: "Australian Youth Climate Coalition",
  BAP: "Building Australia Party",
  CEC: "Citizens Electoral Council of Australia",
  CCP: "Climate Change Coalition",
  CEAA: "Climate Emergency Action Alliance",
  CPA: "Communist Party of Australia",
  DSP: "Democratic Socialist Perspective",
  FFP: "Family First Party",
  FP: "Future Party",
  HEMP: "Help End Marijuana Prohibition (HEMP) Party",
  ICAN: "Independents for Climate Action Now",
  LDP: "Liberal Democratic Party",
  NCPP: "Non-Custodial Parents Party (Equal Parenting)",
  ODD: "Online Direct Democracy",
  PLP: "Progressive Labour Party",
  REP: "Renewable Energy Party",
  RUAP: "Rise Up Australia Party",
  SUPA: "Seniors United Party of Australia",
  SA: "Socialist Alliance",
  SEP: "Socialist Equality Party",
  TAP: "The Arts Party",
  AMHP: "The Australian Mental Health Party",
  TWP: "The Women's Party",
  VEP: "Voluntary Euthanasia Party",
  WAP: "Western Australia Party",
  IND: "Independents",
  JLN: "Jacqui Lambie Network",
};

const MyStats = () => {
  const { isLoggedIn, phoneNo, state: userState } = useContext(UserContext);
  // const { showLoadingWithDelay, hideLoading } = useLoading(); // Commented out

  const [activeTab, setActiveTab] = useState("Federal Leaderboard");
  const [isSharePopupVisible, setIsSharePopupVisible] = useState(false);
  const [shareTableName, setShareTableName] = useState("");
  const [capturedImage, setCapturedImage] = useState(null); // State for storing the captured image

  const [individualNames, setIndividualNames] = useState([]);
  const [individualPercentages, setIndividualPercentages] = useState([]);
  const [partyNames, setPartyNames] = useState([]);
  const [partyPercentages, setPartyPercentages] = useState([]);
  const [stateIndividualNames, setStateIndividualNames] = useState([]);
  const [stateIndividualPercentages, setStateIndividualPercentages] = useState(
    []
  );
  const [statePartyNames, setStatePartyNames] = useState([]);
  const [statePartyPercentages, setStatePartyPercentages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [localProjectData, setLocalProjectData] = useState([]);
  const [individualMPs, setIndividualMPs] = useState([]);
  const [stateIndividualMPs, setStateIndividualMPs] = useState([]);
  const [showVoteCard, setShowVoteCard] = useState(false); // Add this state

  useEffect(() => {
    if (phoneNo) {
      fetchData(phoneNo);
    }
  }, [phoneNo]);

  // Add effect to fetch data when tab changes to leaderboard
  useEffect(() => {
    if (phoneNo && (activeTab === "Federal Leaderboard" || activeTab === "State Leaderboard")) {
      fetchData(phoneNo);
    }
  }, [activeTab]);

  const fetchData = async (phoneNo) => {
    try {
      // showLoadingWithDelay(); // Commented out
      
      // Fetch leaderboard data
      const responseLeaderboard = await fetch(
        `/api/leaderboard?phoneNo=${phoneNo}`
      );
      const leaderboardData = await responseLeaderboard.json();
      
      // Debug log the leaderboard data
      console.log("Federal Leaderboard Data:", leaderboardData);
      
      // Store the full MP objects instead of just names and percentages
      setIndividualMPs(leaderboardData);
      
      // Keep these for backward compatibility
      setIndividualNames(leaderboardData.map((mp) => mp.mp_name));
      setIndividualPercentages(leaderboardData.map((mp) => mp.percentage));

      const responsePartyLeaderboard = await fetch(
        `/api/leaderboard-party?phoneNo=${phoneNo}`
      );
      const partyLeaderboardData = await responsePartyLeaderboard.json();
      setPartyNames(partyLeaderboardData.map((party) => party.party));
      setPartyPercentages(
        partyLeaderboardData.map((party) => party.percentage)
      );

      if (userState) {
        const responseStateLeaderboard = await fetch(
          `/api/leaderboard/mp?phoneNo=${phoneNo}&state=${userState}`
        );
        const stateLeaderboardData = await responseStateLeaderboard.json();
        
        // Debug log the state leaderboard data
        console.log("State Leaderboard Data:", stateLeaderboardData);
        
        // Store the full MP objects for state leaderboard
        setStateIndividualMPs(stateLeaderboardData);
        
        // Keep these for backward compatibility
        setStateIndividualNames(stateLeaderboardData.map((mp) => mp.mp_name));
        setStateIndividualPercentages(
          stateLeaderboardData.map((mp) => mp.percentage)
        );
        
        const responseStatePartyLeaderboard = await fetch(
          `/api/leaderboard-party/state?phoneNo=${phoneNo}&state=${userState}`
        );
        const statePartyLeaderboardData =
          await responseStatePartyLeaderboard.json();
        setStatePartyNames(
          statePartyLeaderboardData.map((party) => party.party)
        );
        setStatePartyPercentages(
          statePartyLeaderboardData.map((party) => party.percentage)
        );
      }

      const responseLocalProjectLeaderboard = await fetch(
        `/api/rank-projects`
      );
      const localProjects = await responseLocalProjectLeaderboard.json();
      setLocalProjectData(localProjects); // Store entire API response
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      // hideLoading(); // Commented out
    }
  };

  const handleDownload = (tableName) => {
    const tableId =
      tableName === "Federal Leaderboard"
        ? "federal-table"
        : tableName === "State Leaderboard"
        ? "state-table"
        : "local-project-table";
  
    const element = document.getElementById(tableId);
    
    if (!element) {
      console.error(`Element with id ${tableId} not found`);
      alert("Unable to download table. Please try again.");
      return;
    }
  
    html2canvas(element, {
      scrollX: 0,
      scrollY: 0,
      useCORS: true,
      logging: true,
      allowTaint: true,
      backgroundColor: null,
      scale: 2
    }).then((canvas) => {
      try {
        const link = document.createElement("a");
        link.download = `${tableName.replace(/\s+/g, "_").toLowerCase()}.png`;
        link.href = canvas.toDataURL("image/png");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error creating download link:", error);
        alert("Failed to generate download. Please try again.");
      }
    }).catch(error => {
      console.error("Error generating canvas:", error);
      alert("Failed to generate image. Please try again.");
    });
  };
  

  const handleShare = async (tableName) => {
    setShareTableName(tableName);
  
    const tableId =
      tableName === "Federal Leaderboard"
        ? "federal-table"
        : tableName === "State Leaderboard"
        ? "state-table"
        : "local-project-table"; // ✅ Add local-project-table
  
    const element = document.getElementById(tableId);
  
    try {
      const canvas = await html2canvas(element, {
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        useCORS: true,
      });
  
      const imageBlob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (!imageBlob) throw new Error("Failed to generate image");
  
      const imageDataUrl = URL.createObjectURL(imageBlob);
      const s3Url = await uploadToBackend(imageDataUrl);
  
      setCapturedImage(s3Url);
      setIsSharePopupVisible(true);
    } catch (error) {
      console.error("Error during image sharing:", error);
      alert("Failed to generate or upload the image.");
    }
  };
  

  const shareToSocialMedia = (platform) => {
    if (!capturedImage) {
      alert("Image is not ready for sharing.");
      return;
    }

    const message = "Check out this leaderboard!";
    const encodedUrl = encodeURIComponent(capturedImage);

    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
          "_blank"
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            message
          )}&url=${encodedUrl}`,
          "_blank"
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
          "_blank"
        );
        break;
      case "whatsapp":
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(
            message
          )}%20${encodedUrl}`,
          "_blank"
        );
        break;
      case "email":
        window.open(
          `mailto:?subject=${encodeURIComponent(
            "Leaderboard Snapshot"
          )}&body=${encodeURIComponent(`${message}\n\n${capturedImage}`)}`,
          "_blank"
        );
        break;
      default:
        alert("Unsupported platform.");
    }

    setIsSharePopupVisible(false);
  };

  const closeSharePopup = () => {
    setIsSharePopupVisible(false);
  };

  // Add handler for Vote Card button
  const handleVoteCardClick = () => {
    setShowVoteCard(true);
  };

  if (!isLoggedIn) {
    return (
      <div
        style={{
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#007bff",
          padding: "20px",
          borderRadius: "5px",
        }}
      >
        Please log in to view details about bills.
      </div>
    );
  }

  return (
    <div className="mystats-container">
      <div className="tabs">
        <button
          className={`tab-button ${
            activeTab === "Federal Leaderboard" ? "active" : ""
          }`}
          onClick={() => setActiveTab("Federal Leaderboard")}
        >
          Federal
        </button>
        <button
          className={`tab-button ${
            activeTab === "State Leaderboard" ? "active" : ""
          }`}
          onClick={() => setActiveTab("State Leaderboard")}
        >
          State
        </button>
        <button
          className={`tab-button ${
            activeTab === "Local Project Leaderboard" ? "active" : ""
          }`}
          onClick={() => setActiveTab("Local Project Leaderboard")}
        >
          Local Project
        </button>
      </div>
      <div className="tab-content">
        {activeTab === "Federal Leaderboard" && (
          <div>
            <ScrollableTable
              id="federal-table"
              individualMPs={individualMPs}
              individualNames={individualNames}
              individualPercentages={individualPercentages}
              partyNames={partyNames}
              partyPercentages={partyPercentages}
            />
            <div className="action-buttons">
              <div 
                style={{ 
                  position: "relative", 
                  display: "inline-block",
                  marginBottom: "40px" // Add space for tooltip
                }}
                onMouseEnter={(e) => {
                  const tooltip = e.currentTarget.querySelector('.tooltip-text');
                  if (tooltip) {
                    tooltip.style.visibility = 'visible';
                    tooltip.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  const tooltip = e.currentTarget.querySelector('.tooltip-text');
                  if (tooltip) {
                    tooltip.style.visibility = 'hidden';
                    tooltip.style.opacity = '0';
                  }
                }}
              >
                <button
                  className="personalised-card-button"
                  onClick={handleVoteCardClick}
                  style={{
                    marginRight: "10px",
                    padding: "8px 16px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    position: "relative"
                  }}
                >
                  How to Vote Card
                </button>
                <div 
                  className="tooltip-text"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                    color: "white",
                    padding: "10px",
                    borderRadius: "5px",
                    fontSize: "12px",
                    width: "300px",
                    textAlign: "center",
                    zIndex: 1000,
                    pointerEvents: "none",
                    opacity: 0,
                    visibility: "hidden",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                    marginTop: "8px",
                    whiteSpace: "normal",
                    lineHeight: "1.4"
                  }}
                >
                  The How to Vote card will be available from EOD 18th of April, once all officially nominated candidates have been released by the AEC and uploaded to the platform.
                </div>
              </div>
              <button
                className="download-button"
                onClick={() => handleDownload("Federal Leaderboard")}
              >
                Download
              </button>
              <button
                className="share-button"
                onClick={() => handleShare("Federal Leaderboard")}
              >
                Share
              </button>
            </div>
          </div>
        )}
        {activeTab === "State Leaderboard" && (
          <div>
            <ScrollableTable
              id="state-table"
              individualMPs={stateIndividualMPs}
              individualNames={stateIndividualNames}
              individualPercentages={stateIndividualPercentages}
              partyNames={statePartyNames}
              partyPercentages={statePartyPercentages}
            />
            <div className="action-buttons">
              <div 
                style={{ 
                  position: "relative", 
                  display: "inline-block",
                  marginBottom: "40px" // Add space for tooltip
                }}
                onMouseEnter={(e) => {
                  const tooltip = e.currentTarget.querySelector('.tooltip-text');
                  if (tooltip) {
                    tooltip.style.visibility = 'visible';
                    tooltip.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  const tooltip = e.currentTarget.querySelector('.tooltip-text');
                  if (tooltip) {
                    tooltip.style.visibility = 'hidden';
                    tooltip.style.opacity = '0';
                  }
                }}
              >
                <button
                  className="personalised-card-button"
                  onClick={handleVoteCardClick}
                  style={{
                    marginRight: "10px",
                    padding: "8px 16px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    position: "relative"
                  }}
                >
                  How to Vote Card
                </button>
                <div 
                  className="tooltip-text"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                    color: "white",
                    padding: "10px",
                    borderRadius: "5px",
                    fontSize: "12px",
                    width: "300px",
                    textAlign: "center",
                    zIndex: 1000,
                    pointerEvents: "none",
                    opacity: 0,
                    visibility: "hidden",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                    marginTop: "8px",
                    whiteSpace: "normal",
                    lineHeight: "1.4"
                  }}
                >
                  The How to Vote card will be available from EOD 18th of April, once all officially nominated candidates have been released by the AEC and uploaded to the platform.
                </div>
              </div>
              <button
                className="download-button"
                onClick={() => handleDownload("State Leaderboard")}
              >
                Download
              </button>
              <button
                className="share-button"
                onClick={() => handleShare("State Leaderboard")}
              >
                Share
              </button>
            </div>
          </div>
        )}
        {activeTab === "Local Project Leaderboard" &&
          (() => {
            // Function to filter projects based on search term
            const getFilteredProjects = () => {
              const searchValue = searchTerm.toLowerCase();
              return localProjectData.filter((project) =>
                [
                  project.project_title,
                  project.category,
                  project.funds_raised,
                  project.unique_backers,
                ].some((field) =>
                  field?.toString().toLowerCase().includes(searchValue)
                )
              );
            };

            return (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Search Bar */}
                <div
                  className="search-bar"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                    width: "100%",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search projects..."
                    className="search-input"
                    style={{
                      flex: "1",
                      padding: "10px",
                      fontSize: "14px",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                      outline: "none",
                    }}
                    onChange={(e) =>
                      setSearchTerm(e.target.value.toLowerCase())
                    }
                  />
                  <button
                    className="search-button"
                    style={{
                      marginLeft: "10px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      background: "linear-gradient(to right, #007bff, #00d4ff)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      transition: "background-color 0.3s",
                    }}
                  >
                    Search
                  </button>
                </div>

                {/* Local Project Table */}
                <div
                  className="scrollable-table"
                  id="local-project-table"
                  style={{ flex: "1", overflowY: "auto", height: "100%" }}
                >
                  <table
                    className="leaderboard-table"
                    style={{ width: "100%" }}
                  >
                    <thead>
                      <tr>
                        <th>Ranking</th>
                        <th>Project Name</th>
                        <th>Category Name</th>
                        <th>Total Raised</th>
                        <th>No. Backers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredProjects().map((project, index) => (
                        <tr key={index}>
                          <td>{project.rank}</td>
                          <td>{project.project_title}</td>
                          <td>{project.category || "N/A"}</td>
                          <td>${project.funds_raised || 0}</td>
                          <td>{project.unique_backers || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 🔥 Add Download & Share Buttons Here */}
                <div className="action-buttons">
                  <button
                    className="download-button"
                    onClick={() => handleDownload("Local Project Leaderboard")}
                  >
                    Download
                  </button>
                  <button
                    className="share-button"
                    onClick={() => handleShare("Local Project Leaderboard")}
                  >
                    Share
                  </button>
                </div>
              </div>
            );
          })()}
      </div>

      {/* Add Vote Card popup */}
      {showVoteCard && (
        <VoteCard
          onClose={() => setShowVoteCard(false)}
          activeTab={activeTab}
          individualMPs={activeTab === "Federal Leaderboard" ? individualMPs : stateIndividualMPs}
          partyNames={activeTab === "Federal Leaderboard" ? partyNames : statePartyNames}
          partyPercentages={activeTab === "Federal Leaderboard" ? partyPercentages : statePartyPercentages}
        />
      )}

      {/* Share Popup */}
      {isSharePopupVisible && (
        <div className="share-popup">
          <div className="popup-header">
            <h3>Share your leaderboard</h3>
            <button className="close-button" onClick={closeSharePopup}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="popup-icons">
            <FontAwesomeIcon
              icon={faFacebook}
              title="Facebook"
              className="share-icon facebook"
              onClick={() => shareToSocialMedia("facebook")}
            />
            <FontAwesomeIcon
              icon={faXTwitter}
              title="Twitter"
              className="share-icon twitter"
              onClick={() => shareToSocialMedia("twitter")}
            />
            <FontAwesomeIcon
              icon={faWhatsapp}
              title="WhatsApp"
              className="share-icon whatsapp"
              onClick={() => shareToSocialMedia("whatsapp")}
            />
            {/* <FontAwesomeIcon
              icon={faEnvelope}
              title="Email"
              className="share-icon email"
              onClick={() => shareToSocialMedia("email")}
            /> */}
          </div>
        </div>
      )}
    </div>
  );
};

const ScrollableTable = ({
  id,
  individualMPs = [],
  individualNames,
  individualPercentages,
  partyNames,
  partyPercentages,
}) => {
  const [hoveredParty, setHoveredParty] = useState(null);

  const mps = individualMPs.length > 0 
    ? individualMPs 
    : individualNames.map((name, index) => ({
        mp_name: name,
        percentage: individualPercentages[index],
        is_candidate: false // Default to false when using legacy data
      }));

  // Check if data is loaded
  const isDataLoaded = mps.length > 0 && partyNames.length > 0;

  const MPRow = ({ mp }) => {
    // Add debug logging
    console.log(`Rendering MP: ${mp.mp_name}, Is Candidate: ${mp.is_candidate}`);
    
    // Force Zirui Liu to be marked as a candidate in the UI
    const isCandidate = mp.is_candidate || mp.mp_name === 'Zirui Liu';
    const partyName = partyNames[mps.indexOf(mp)] || '';
    
    return (
      <tr>
        <td>
          <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
            {mp.mp_name}
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{
                marginLeft: '5px',
                color: 'black',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                const tooltip = e.currentTarget.nextElementSibling;
                if (tooltip) tooltip.style.display = 'block';
              }}
              onMouseLeave={(e) => {
                const tooltip = e.currentTarget.nextElementSibling;
                if (tooltip) tooltip.style.display = 'none';
              }}
            />
            <div style={{
              display: 'none',
              position: 'absolute',
              left: '100%',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              zIndex: 1000,
              marginLeft: '10px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <div><strong>Party:</strong> {mp.party || 'N/A'}</div>
              <div><strong>Chamber:</strong> {mp.chamber || 'N/A'}</div>
              <div><strong>Constituency:</strong> {mp.constituency || 'N/A'}</div>
            </div>
          </div>
          {isCandidate && (
            <span 
              style={{
                backgroundColor: '#FFD700',
                color: '#000',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.8rem',
                marginLeft: '8px',
                fontWeight: 'bold',
                display: 'inline-block'
              }} 
              title="Candidate"
            >
              <FaUserTie style={{ marginRight: '4px' }} /> Candidate
            </span>
          )}
        </td>
        <td>{mp.percentage.toFixed(2)}%</td>
        <td style={{ position: 'relative' }}>
          {partyName}
          {partyName && (
            <span style={{ position: 'relative', display: 'inline-block' }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  marginLeft: '5px',
                  color: 'black',
                  cursor: 'pointer'
                }}
                onMouseEnter={() => setHoveredParty(partyName)}
                onMouseLeave={() => setHoveredParty(null)}
              />
              {hoveredParty === partyName && (
                <div style={{
                  position: 'absolute',
                  left: '100%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  zIndex: 1000,
                  marginLeft: '5px'
                }}>
                  {partyFullForm[partyName] || partyName}
                </div>
              )}
            </span>
          )}
        </td>
        <td>{partyPercentages[mps.indexOf(mp)] ? partyPercentages[mps.indexOf(mp)].toFixed(2) + '%' : ''}</td>
      </tr>
    );
  };

  return (
    <div className="scrollable-table" id={id}>
      <table className="leaderboard-table">
        <thead>
          <tr>
            <th colSpan="2">MP's Aligned with Your Vote</th>
            <th colSpan="2">Parties Aligned with Your Vote</th>
          </tr>
          <tr>
            <th>Sitting Members</th>
            <th>%</th>
            <th>Political Parties</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          {!isDataLoaded ? (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Loading...
              </td>
            </tr>
          ) : (
            mps.map((mp, index) => (
              <MPRow key={index} mp={mp} />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MyStats;

<style>
  {`
    .action-buttons {
      position: relative;
      z-index: 1;
    }
    
    .tooltip-text {
      z-index: 9999;
    }

    .tooltip-text::before {
      content: '';
      position: absolute;
      top: -8px;
      left: 50%;
      transform: translateX(-50%);
      border-width: 4px;
      border-style: solid;
      border-color: transparent transparent rgba(0, 0, 0, 0.9) transparent;
    }
  `}
</style>